/**
 * push.ts — Browser-seitige Web-Push-Helper
 *
 * Stellt Funktionen für Service-Worker-Registrierung, Permission-Handling
 * und Push-Subscription-Management bereit. Diese Datei kapselt ALLE
 * Browser-APIs rund um Web-Push (Notification, ServiceWorker, PushManager)
 * und enthält KEINE Supabase- oder React-Abhängigkeiten.
 *
 * Architektur:
 *   push.ts          (diese Datei)        → Browser-Push-API
 *   supabase/push.js (Phase 4.3)          → Supabase-CRUD für push_subscriptions
 *   NotificationSettings.jsx (Phase 4.5)  → UI, kombiniert beide oben
 *
 * Standards:
 *   - RFC 8030 (Web Push Protocol)
 *   - RFC 8291 (Message Encryption)
 *   - W3C Push API
 *
 * iOS-Hinweis:
 *   Web-Push funktioniert auf iOS Safari NUR wenn die PWA über
 *   "Zum Home-Bildschirm hinzufügen" installiert wurde. In normalen
 *   Browser-Tabs liefert isPushSupported() auf iOS Safari false zurück.
 */

import { geraetAusUserAgent } from './geraet.js';

const SW_PATH = '/sw.js';

/**
 * Push-Permission-State inklusive `'unsupported'` für Browser ohne Push-API.
 * `NotificationPermission` ist der built-in DOM-Typ `'default' | 'granted' | 'denied'`.
 */
export type PushPermissionState = NotificationPermission | 'unsupported';

/**
 * DB-Format einer Push-Subscription (wie in der `push_subscriptions`-Tabelle
 * gespeichert). Alle drei Felder sind Pflicht — eine Subscription ohne
 * `p256dh`/`auth` wäre für den Server unbrauchbar.
 */
export interface PushDbSubscription {
  endpoint: string;
  p256dh: string;
  auth: string;
}

// ═══════════════════════════════════════════════════════════════════
// CAPABILITY CHECKS
// ═══════════════════════════════════════════════════════════════════

/**
 * Prüft, ob der Browser Web-Push überhaupt unterstützt.
 *
 * Voraussetzungen:
 *   - serviceWorker API
 *   - PushManager API
 *   - Notification API
 *   - HTTPS oder localhost (wird vom Browser selbst geprüft)
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Gibt den aktuellen Permission-State zurück, ohne den User zu fragen.
 */
export function getPushPermissionState(): PushPermissionState {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// ═══════════════════════════════════════════════════════════════════
// SERVICE WORKER REGISTRIERUNG
// ═══════════════════════════════════════════════════════════════════

/**
 * Registriert den DRPX Service Worker (`/sw.js`).
 *
 * Diese Funktion ist idempotent: mehrfaches Aufrufen ist sicher,
 * der Browser nutzt automatisch eine bereits laufende Registrierung.
 *
 * Wirft einen Fehler, wenn:
 *   - Push nicht unterstützt wird (`push_unsupported`)
 *   - Die SW-Datei nicht geladen werden kann, z.B. 404 (`sw_registration_failed`)
 *   - HTTPS fehlt (außer localhost) (`sw_registration_failed`)
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!isPushSupported()) {
    throw new Error('push_unsupported');
  }
  try {
    const registration = await navigator.serviceWorker.register(SW_PATH);
    // Auf "ready" warten, damit der SW garantiert aktiv ist bevor
    // wir versuchen eine Subscription zu erstellen
    await navigator.serviceWorker.ready;
    return registration;
  } catch (err: unknown) {
    console.error('[push] Service Worker registration failed:', err);
    throw new Error('sw_registration_failed');
  }
}

// ═══════════════════════════════════════════════════════════════════
// PERMISSION + SUBSCRIPTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Fragt den User um Push-Permission und erstellt eine Push-Subscription.
 *
 * Ablauf:
 *   1. Prüfe Browser-Support
 *   2. Stelle sicher, dass SW registriert ist
 *   3. Frage Permission (Browser-Dialog erscheint)
 *   4. Erstelle PushSubscription mit dem VAPID Public Key
 *   5. Gib die Subscription als DB-kompatibles Objekt zurück
 *
 * Wirft einen Fehler bei:
 *   - `push_unsupported` (Browser kann's nicht)
 *   - `vapid_key_missing` (kein VAPID Key übergeben)
 *   - `permission_denied` (User hat abgelehnt)
 *   - `permission_dismissed` (User hat den Dialog geschlossen)
 *   - `subscription_failed` (technischer Fehler beim Erstellen)
 *   - `subscription_invalid` (Browser lieferte Subscription ohne keys)
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushDbSubscription> {
  if (!isPushSupported()) {
    throw new Error('push_unsupported');
  }
  if (!vapidPublicKey || typeof vapidPublicKey !== 'string') {
    throw new Error('vapid_key_missing');
  }

  // 1. Service Worker registrieren (idempotent)
  const registration = await registerServiceWorker();

  // 2. Permission abfragen — ggf. erscheint der Browser-Dialog
  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    throw new Error('permission_denied');
  }
  if (permission !== 'granted') {
    // 'default' = User hat den Dialog geschlossen ohne zu wählen
    throw new Error('permission_dismissed');
  }

  // 3. Push-Subscription erstellen
  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // Pflicht: jede Push muss sichtbar sein
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  } catch (err: unknown) {
    console.error('[push] Subscription failed:', err);
    throw new Error('subscription_failed');
  }

  // 4. In das Format für die Datenbank umwandeln
  return subscriptionToDbFormat(subscription);
}

/**
 * Beendet die aktuelle Push-Subscription im Browser.
 * Hat keinen Effekt wenn keine Subscription existiert.
 *
 * Wichtig: Diese Funktion löscht NUR im Browser. Den Eintrag in der
 * `push_subscriptions`-Tabelle muss der Aufrufer separat löschen
 * (Phase 4.3, `supabase/push.js`).
 *
 * @returns `true` wenn etwas zu beenden war, sonst `false`
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;
    await subscription.unsubscribe();
    return true;
  } catch (err: unknown) {
    console.error('[push] Unsubscribe failed:', err);
    return false;
  }
}

/**
 * Holt die aktuelle Push-Subscription des Browsers, falls vorhanden.
 * Nützlich um beim App-Start zu prüfen, ob der User bereits aktiviert hat.
 *
 * @returns Die Subscription im DB-Format, oder `null` wenn keine existiert
 *   bzw. wenn Push nicht unterstützt wird. Eine Subscription ohne gültige
 *   Keys wird ebenfalls als `null` behandelt (defensiv).
 */
export async function getCurrentSubscription(): Promise<PushDbSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return null;
    return subscriptionToDbFormat(subscription);
  } catch (err: unknown) {
    console.error('[push] getCurrentSubscription failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════
// DEVICE LABEL (datensparsam)
// ═══════════════════════════════════════════════════════════════════

/**
 * Generiert eine zusammengefasste Geräte-Bezeichnung wie
 * "Chrome auf Windows" oder "Safari auf iPhone".
 *
 * Datenschutz: Wir speichern KEINEN vollständigen User-Agent
 * (der wäre eindeutig fingerprint-bar). Stattdessen reduzieren wir
 * auf zwei Achsen: Browser-Familie + OS-Familie.
 */
export function getDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Unbekanntes Gerät';

  // Erkennung gemeinsam mit der Admin-Ansicht „Geräte" (lib/geraet.ts,
  // 15.10.2026). Ausgabe unverändert: Rückfälle „Browser" / „Gerät".
  const { browser, system } = geraetAusUserAgent(navigator.userAgent);

  return (browser || 'Browser') + ' auf ' + (system || 'Gerät');
}

// ═══════════════════════════════════════════════════════════════════
// INTERNE HELPER
// ═══════════════════════════════════════════════════════════════════

/**
 * Wandelt ein PushSubscription-Objekt in das Format um, das in der
 * `push_subscriptions`-Tabelle gespeichert wird.
 *
 * Wirft `subscription_invalid`, wenn der Browser eine Subscription ohne
 * vollständige Keys liefert — so eine Subscription wäre für den Server
 * unbrauchbar. Dieser Pfad sollte in der Praxis nie erreicht werden,
 * aber ein ehrlich typisierter Return (`PushDbSubscription` statt
 * `{...keys: string | undefined}`) ist es wert, den Edge-Case strukturiert
 * abzufangen.
 */
function subscriptionToDbFormat(subscription: PushSubscription): PushDbSubscription {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys || !json.keys.p256dh || !json.keys.auth) {
    throw new Error('subscription_invalid');
  }
  return {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  };
}

/**
 * Wandelt einen Base64URL-String (VAPID Public Key) in ein `Uint8Array` um,
 * wie es `PushManager.subscribe()` erwartet.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  // Expliziter, nicht-shared ArrayBuffer — wird von
  // PushManager.subscribe().applicationServerKey strikt verlangt
  // (BufferSource erlaubt keinen SharedArrayBuffer). Ohne diesen
  // expliziten Buffer würde TypeScript 5.7+ den Typ als
  // Uint8Array<ArrayBufferLike> inferieren, was nicht zuweisbar ist.
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
